import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { sendInvoiceReport } from 'services/billingServices';
import { Button } from 'primereact/button';
import _ from 'lodash';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import { InvoicePrintView } from './InvoicePrintView';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { dollarFormat, getTenantDetails, toCapitalized } from 'utils/utils';
import { convertObjectToQuery } from 'utils/utils';
import { formatPhoneNumber } from 'utils/utils';
import { reduceTax } from 'utils/utility_functions/commonCalculations';
import { useActivityLog } from 'hooks/useActivityLog';
import { generateFullName } from 'utils/generateFullNameUtils';
import constants from 'constants/index';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';

function FlatFeeNoteCell({ note }) {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    if (!note) {
      setShowToggle(false);
      return;
    }

    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        const isOverflowing = element.scrollHeight > element.clientHeight + 1;
        setShowToggle(isOverflowing);
      }
    };

    checkOverflow();

    const timeoutId = setTimeout(checkOverflow, 0);

    return () => clearTimeout(timeoutId);
  }, [note]);

  if (!note) return null;

  return (
    <div className={`ffn-note-wrapper${expanded ? ' expanded' : ''}`}>
      <div ref={textRef} className="ffn-note-text" aria-expanded={expanded} title={note}>
        {note}
      </div>
      {showToggle && (
        <button type="button" className="ffn-toggle-btn btn btn-link p-0" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}

export const ViewEditInvoice = (props) => {
  const { addActivity } = useActivityLog();
  const { userDetails } = useUserDetailsContext();

  const [invoiceDetails, setInvoiceDetails] = useState();
  let componentRef = useRef(null);
  const { addToast } = useToast();
  const paymentStructure = {
    FLAT_FEE: 'Flat Fee',
    SUBSCRIPTION: 'Subscription',
    CONTINGENCY_FEE: 'Contingency Fee',
    EVERGREEN_RETAINER: 'Evergreen Retainer',
    STANDARD_RETAINER: 'Standard Retainer',
  };
  useEffect(() => {
    if (props.invoiceDetails) {
      setInvoiceDetails({ ...props.invoiceDetails });
    } else {
      setInvoiceDetails();
    }
  }, [props.invoiceDetails]);

  const createActivityLog = () => {
    const [tenantId] = getTenantDetails();
    const data = {
      event_key: 'Read',
      event_type: 'invoice',
      event_subtype: 'read_invoice',
      message: `Viewed Invoice: ${props.invoiceDetails?.invoice?.invoice_number}`,
      event_status: 'success',
      event_items: {
        case_id: props.invoiceDetails?.invoice?.case_id,
        tenant_id: tenantId,
        case_name: props?.invoiceDetails?.invoice?.case_name,
      },
    };
    addActivity(data);
  };

  const sendInvoice = () => {
    const { pk, sk, invoice } = props.invoiceDetails;
    const params = {
      contact_id: invoice?.contact_id,
      invoice_pk: pk,
      invoice_sk: sk,
      is_lead: props?.isLeadSpecific ? props?.isLeadSpecific : props?.invoiceDetails?.is_lead,
    };
    sendInvoiceReport(convertObjectToQuery(params))
      .then(() => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.SEND_INVOICE_SUCCESS);
        props?.loadInvoices(props?.filters, props?.getPageLimit() ?? 10, '', 'forward');
      })
      .catch((err) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.SEND_INVOICE_FAILED);
      });
  };

  useEffect(() => {
    //call createActivityLog function after 5 seconds
    const timer = setTimeout(() => {
      createActivityLog();
    }, 5000);
    return () => clearTimeout(timer);
  }, [props?.invoiceDetails]);

  return (
    <div className="invoice-container">
      {invoiceDetails && (
        <div>
          <div style={{ display: 'none' }}>
            <div ref={componentRef} id="invoicePrint">
              <InvoicePrintView invoiceDetails={props.invoiceDetails} />
            </div>
          </div>

          <div className="">
            <div className="col-12 invoice-action-buttons">
              {props.invoiceDetails?.invoice_status !== 'FORWARDED' && (
                <Button
                  className="p-button-secondary outline"
                  label={'Edit'}
                  onClick={() => {
                    props.setIsEdit(true);
                  }}
                  disabled={props?.invoiceDetails?.invoice_status === 'PAID'}
                ></Button>
              )}

              <ReactToPrint content={() => componentRef.current}>
                <PrintContextConsumer>
                  {({ handlePrint }) => (
                    <Button className="p-button-secondary outline " onClick={handlePrint}>
                      Print
                    </Button>
                  )}
                </PrintContextConsumer>
              </ReactToPrint>
              <Button
                className="p-button-secondary outline"
                label={'Record Payment'}
                onClick={() => {
                  props?.setShowAcceptPaymentModal(true);
                }}
                disabled={props?.invoiceDetails?.invoice_status === 'FORWARDED'}
              />

              <Button
                className="p-button-secondary outline"
                label={'Send Invoice'}
                onClick={sendInvoice}
                disabled={props?.invoiceDetails?.invoice_status === 'PAID' || props?.invoiceDetails?.invoice_status === 'FORWARDED'}
              />
            </div>
          </div>
          <div>
            <div className="row view-invoice-wrap F-size14">
              <div className="col-12 col-md-5">
                {!!invoiceDetails?.contingency?.contingency_data ? (
                  <div className="caption-bold mb-2 mt-4">Client Name</div>
                ) : (
                  <div className="caption-bold mb-2 mt-4">billed from</div>
                )}

                {invoiceDetails.created_by_details && (
                  <div className="card-bg p-3">
                    <div className="col-12 pt-2 text-bold">{generateFullName(invoiceDetails.created_by_details)}</div>
                    {invoiceDetails?.billed_from?.address && (
                      <>
                        <div className="col-12 ">{invoiceDetails.billed_from?.address?.office_name}</div>
                        <div className="col-12">
                          {invoiceDetails.billed_from?.address?.address1}
                          {invoiceDetails.billed_from?.address?.address2 ? `, ${invoiceDetails.billed_from?.address?.address2}` : ''}
                        </div>
                        <div className="col-12 ">{invoiceDetails?.billed_from?.address.street}</div>
                        <div className="col-12 ">{invoiceDetails?.billed_from?.address?.city}</div>
                        <div className="col-12">{invoiceDetails?.billed_from?.address?.office_state}</div>
                        <div className="col-12 ">{invoiceDetails?.billed_from?.address?.country}</div>
                        <div className="col-12 ">{formatPhoneNumber(invoiceDetails.billed_from?.address?.main_phone)}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {!invoiceDetails?.contingency?.contingency_data && (
                <div className="col-md-4 px-2">
                  <div className="caption-bold mb-2 mt-4">billed to</div>
                  <div className="card-bg p-3">
                    {invoiceDetails?.invoice && (
                      <>
                        <div className="col-12 py-2">{invoiceDetails?.invoice?.contact_name} </div>
                      </>
                    )}

                    <div className="col-12">{invoiceDetails.invoice?.street} </div>
                    <div className="col-12">{invoiceDetails.invoice?.city}</div>
                    <div className="col-12">{invoiceDetails.invoice?.state} </div>
                    <div className="col-12">{invoiceDetails.invoice?.country}</div>
                    <div className="col-12">{formatPhoneNumber(invoiceDetails.invoice?.phone)}</div>
                    <div className="col-12">{invoiceDetails.invoice?.zip_code ? `Zip - ${invoiceDetails.invoice?.zip_code}` : ''}</div>
                  </div>
                </div>
              )}
              <div className="col-12 col-md-5 justify-content-end ">
                <div className="caption-bold mb-2 mt-4">invoice information</div>
                <div className="p-3 card-bg">
                  <div className="d-flex mb-2">
                    <div className="fw-bold flex-grow-1">Invoice #</div>
                    <div>{invoiceDetails.invoice_number}</div>
                  </div>

                  <div className="d-flex mb-2">
                    <div className="fw-bold flex-grow-1">Invoice Date</div>
                    <div>
                      {invoiceDetails.invoice?.invoice_date
                        ? handleDateTimeOffset(
                            userDetails?.timezone,
                            invoiceDetails.invoice?.invoice_date,
                            constants.month_date_year_format
                          )
                        : ''}
                    </div>
                  </div>

                  {!invoiceDetails?.contingency?.contingency_data && (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Due Date</div>
                      <div>
                        {invoiceDetails?.invoice?.due_date
                          ? handleDateTimeOffset(userDetails?.timezone, invoiceDetails?.invoice?.due_date, constants.month_date_year_format)
                          : ''}
                      </div>
                    </div>
                  )}

                  {!!invoiceDetails?.flat_fees?.total_amount && (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">
                        {invoiceDetails?.payment_structure?.toLowerCase() === paymentStructure.FLAT_FEE?.toLowerCase()
                          ? 'Total Flat Fee'
                          : 'Total Retainer Fee'}
                      </div>
                      <div>{dollarFormat(invoiceDetails?.flat_fees?.total_amount)}</div>
                    </div>
                  )}

                  {!!invoiceDetails?.contingency?.contingency_data && typeof invoiceDetails?.settlement !== 'undefined' ? (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Amount to Client</div>
                      <div>{dollarFormat(invoiceDetails?.settlement?.check_to_client)}</div>
                    </div>
                  ) : (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Balance Due</div>
                      <div>{dollarFormat(invoiceDetails.due_amount)}</div>
                    </div>
                  )}

                  {!invoiceDetails?.contingency?.contingency_data && (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Payment Terms</div>
                      <div>{invoiceDetails.invoice?.payment_terms || ''}</div>
                    </div>
                  )}

                  <div className="d-flex mb-2">
                    <div className="fw-bold flex-grow-1">Case / Matter</div>
                    <div>{invoiceDetails.invoice?.case_name}</div>
                  </div>

                  <div className="d-flex mb-2">
                    <div className="fw-bold flex-grow-1">Status</div>
                    <div>{invoiceDetails.invoice_status ? _.startCase(_.toLower(invoiceDetails.invoice_status)) : ''}</div>
                  </div>

                  <div className="d-flex mb-2">
                    <div className="fw-bold flex-grow-1">Current Trust Balance</div>
                    <div>
                      {invoiceDetails?.invoice?.trust_balance_details &&
                      Object.keys(invoiceDetails.invoice.trust_balance_details)?.length > 0 &&
                      parseFloat(invoiceDetails?.invoice?.trust_balance_details?.trust_data[0]?.trust_balance)
                        ? dollarFormat(invoiceDetails?.invoice?.trust_balance_details?.trust_data[0]?.trust_balance)
                        : dollarFormat(0)}
                    </div>
                  </div>

                  {invoiceDetails.invoice?.check_date && (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Check Date</div>
                      <div>
                        {handleDateTimeOffset(userDetails?.timezone, invoiceDetails.invoice?.check_date, constants.month_date_year_format)}
                      </div>
                    </div>
                  )}

                  {invoiceDetails?.invoice?.check_number && (
                    <div className="d-flex mb-2">
                      <div className="fw-bold flex-grow-1">Check Number</div>
                      <div>{invoiceDetails.invoice?.check_number}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              {invoiceDetails?.flat_fees?.flat_fee_data && invoiceDetails?.flat_fees?.flat_fee_data.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">
                    {invoiceDetails?.payment_structure?.toLowerCase() === paymentStructure.FLAT_FEE?.toLowerCase()
                      ? 'Flat Fees'
                      : 'Retainer Fees'}
                  </div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Members</th>
                            <th>Item</th>
                            <th>
                              {invoiceDetails?.payment_structure?.toLowerCase() === paymentStructure.FLAT_FEE?.toLowerCase()
                                ? 'Flat Fee Notes'
                                : 'Retainer Fee Notes'}
                            </th>
                            {!!invoiceDetails?.flat_fees?.tax_percentage ? (
                              <>
                                <th>Actual Amount</th>
                                <th>Amount (with Tax)</th>
                              </>
                            ) : (
                              <>
                                <th>Amount</th>
                              </>
                            )}
                            <th>Billable</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.flat_fees?.flat_fee_data.map((invoiceData, index) => {
                          return (
                            <tbody>
                              <tr key={index}>
                                <td>
                                  {invoiceData.flat_fee_date
                                    ? handleDateTimeOffset(
                                        userDetails?.timezone,
                                        invoiceData.flat_fee_date,
                                        constants.month_date_year_format
                                      )
                                    : ''}
                                </td>
                                <td>
                                  {invoiceData?.employee_name && !invoiceData?.employee_name.includes('undefined')
                                    ? invoiceData?.employee_name
                                    : ''}
                                </td>
                                <td>{invoiceData.item_name}</td>
                                <td className="invoice-description-col">
                                  <FlatFeeNoteCell note={invoiceData.flat_fee_notes} />
                                </td>
                                {!!invoiceDetails?.flat_fees?.tax_percentage && (
                                  <td>{dollarFormat(reduceTax(invoiceData.flat_fee_amount, invoiceDetails?.flat_fees?.tax_percentage))}</td>
                                )}
                                <td>{dollarFormat(invoiceData.flat_fee_amount)}</td>
                                <td>
                                  <label className="switch">
                                    <input type="checkbox" checked={invoiceData?.billable} name="non_billable" disabled />
                                    <span className="slider round"></span>
                                  </label>
                                </td>
                              </tr>
                            </tbody>
                          );
                        })}
                        <tfoot className="">
                          <tr>
                            <td></td>
                            <td></td>
                            {!!invoiceDetails?.flat_fees?.tax_percentage ? (
                              <>
                                <td></td>
                                <td className="text-bold">Total</td>
                                <td className="text-bold">
                                  {dollarFormat(
                                    (invoiceDetails?.flat_fees?.flat_fee_data).reduce((accumulator, currentValue) => {
                                      if (!!currentValue?.tax_applied) {
                                        accumulator += parseFloat(
                                          reduceTax(currentValue?.flat_fee_amount, invoiceDetails?.flat_fees?.tax_percentage)
                                        );
                                      } else {
                                        accumulator += parseFloat(currentValue?.flat_fee_amount);
                                      }
                                      return accumulator;
                                    }, 0)
                                  )}
                                </td>
                                <td className="text-bold">{dollarFormat(invoiceDetails?.flat_fees?.total_amount)}</td>
                              </>
                            ) : (
                              <>
                                <td></td>
                                <td className="text-bold">Total</td>
                                <td className="text-bold">{dollarFormat(invoiceDetails?.flat_fees?.total_amount)}</td>
                              </>
                            )}
                            <td></td>
                          </tr>
                          {!!invoiceDetails?.flat_fees?.tax_percentage && (
                            <tr>
                              <td className="disclaimer">Tax Rate @ {invoiceDetails?.flat_fees?.tax_percentage}%</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.time_entries?.time_entry_data?.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Time Entries</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Members</th>
                            <th>Case</th>
                            <th>Activity</th>
                            <th>Description</th>
                            <th>Fee</th>
                            <th>Duration</th>
                            <th>Billable</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.time_entries?.time_entry_data.map((index) => {
                          let name = index?.created_by_details?.first_name;
                          if (index?.created_by_details?.last_name) name = name + ' ' + index?.created_by_details?.last_name;
                          let hour_rate = index.hour_rate;
                          hour_rate = hour_rate ? parseFloat(hour_rate) : 0;
                          let total_hours = index.total_hours;
                          total_hours = total_hours ? parseFloat(total_hours) : 0;
                          let total = hour_rate * total_hours;
                          return (
                            <tbody>
                              <tr>
                                <td>
                                  {index.time_entry_date
                                    ? handleDateTimeOffset(userDetails?.timezone, index.time_entry_date, constants.month_date_year_format)
                                    : ''}
                                </td>
                                <td>{name}</td>
                                <td>{index.case_name}</td>
                                <td>
                                  {index.activity_type
                                    ? // If activity_type_code is null/not there, show only activity_type without hyphen
                                      !index.activity_type_code || index.activity_type_code === 'null' || index.activity_type_code === null
                                      ? index.activity_type
                                      : `${index.activity_type_code}-${index.activity_type}`
                                    : '-'}
                                </td>
                                <td className="invoice-description-col">
                                  <FlatFeeNoteCell note={index.notes} />
                                </td>
                                <td>{dollarFormat(index.hour_rate)}</td>
                                <td>{index.total_hours}</td>
                                <td>
                                  <label className="switch">
                                    <input type="checkbox" checked={index?.billable} name="non_billable" disabled />
                                    <span className="slider round"></span>
                                  </label>
                                </td>
                                <td>{dollarFormat(total)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                        <tfoot className="">
                          <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td className="text-bold">Total hours:</td>
                            <td className="text-bold">{invoiceDetails?.time_entries?.total_hours}</td>
                            <td className="text-bold">Total Amount</td>
                            <td className="text-bold">{dollarFormat(invoiceDetails?.time_entries?.total_amount)}</td>
                          </tr>
                          {(invoiceDetails?.payment_structure === paymentStructure.EVERGREEN_RETAINER ||
                            invoiceDetails?.payment_structure === paymentStructure.STANDARD_RETAINER) &&
                            !!invoiceDetails?.time_entries?.tax_percentage &&
                            !!invoiceDetails?.time_entries?.total_amount_with_tax && (
                              <>
                                <tr>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td className="text-bold">Tax %:</td>
                                  <td className="text-bold">{invoiceDetails?.time_entries?.tax_percentage}%</td>
                                  <td className="text-bold">Total Amount + Tax</td>
                                  <td className="text-bold">{dollarFormat(invoiceDetails?.time_entries?.total_amount_with_tax)}</td>
                                </tr>
                              </>
                            )}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.expenses?.expense_data && invoiceDetails?.expenses?.expense_data.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Expenses</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Members</th>
                            <th>Case</th>
                            <th>Expense Type</th>
                            <th>Description</th>
                            <th>Fee Type</th>
                            <th>Fee</th>
                            <th>Quantity</th>
                            <th>Billable</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.expenses?.expense_data.map((index) => {
                          return (
                            <tbody>
                              <tr>
                                <td>
                                  {index.expense_date
                                    ? handleDateTimeOffset(userDetails?.timezone, index.expense_date, constants.month_date_year_format)
                                    : ''}
                                </td>
                                <td>{index.team_member_name}</td>
                                <td>{index.case_name}</td>
                                <td>{index.expense_type}</td>
                                <td className="invoice-description-col">
                                  <FlatFeeNoteCell note={index.description} />
                                </td>
                                <td className="text-capitalize">{index.fee_type || 'Hourly'}</td>
                                <td>{index.fee_type === 'fixed' ? '-' : dollarFormat(index.rate_per_unit)}</td>
                                <td>{index.fee_type === 'fixed' ? '-' : index.quantity}</td>
                                <td>
                                  <label className="switch">
                                    <input type="checkbox" checked={index?.billable} name="non_billable" disabled />
                                    <span className="slider round"></span>
                                  </label>
                                </td>
                                <td>{dollarFormat(index.total_amount)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                        <tfoot className="">
                          <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td className="text-bold">Total Amount</td>
                            <td className="text-bold">{dollarFormat(invoiceDetails?.expenses?.total_amount)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!!invoiceDetails?.case_expenses && invoiceDetails?.case_expenses.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Case Expenses</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Vendor</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.case_expenses.map((data) => {
                          return (
                            <tbody>
                              <tr>
                                <td>{data?.case_vendor?.vendor?.title}</td>
                                <td>{dollarFormat(data?.case_vendor?.bill_amount)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                        <tfoot>
                          <tr>
                            <td className="text-bold">Total</td>
                            <td className="text-bold">{dollarFormat(invoiceDetails?.settlement?.total_case_expense)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!!invoiceDetails?.medical_bills && invoiceDetails?.medical_bills.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Medical Bills</div>
                  <div className="col-12 card-bg">
                    <div>
                      <div className="d-flex">
                        <div className="flex-grow-1">Provider</div>
                        <div>Amount</div>
                      </div>

                      {invoiceDetails?.medical_bills.map((item, idx) => (
                        <div className="d-flex" key={idx}>
                          <div className="flex-grow-1">{item?.medical_provider?.provider?.title || '-'}</div>
                          <div>{dollarFormat(item?.medical_provider?.bill_amount)}</div>
                        </div>
                      ))}

                      <div className="d-flex">
                        <div className="flex-grow-1 fw-bold">Total</div>
                        <div className="fw-bold">{dollarFormat(invoiceDetails?.settlement?.balance_owed)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.settlement && (
                <div className="mb-3 col-12 col-sm-4">
                  <div className="card-bg p-3 align-self-start">
                    <div className="d-flex align-items-center py-2">
                      <div className="col-5">Total Bills And Expenses:</div>
                      <div className="col-7 d-flex justify-content-end text-bold">
                        {dollarFormat(
                          parseFloat(invoiceDetails?.settlement?.balance_owed) + parseFloat(invoiceDetails?.settlement?.total_case_expense)
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.adjustments?.adjustment_data && invoiceDetails?.adjustments?.adjustment_data.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Adjustments</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Applied To</th>
                            <th>Type</th>
                            <th>Notes</th>
                            <th>Basis</th>
                            <th>Percentage</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.adjustments?.adjustment_data.map((index) => {
                          return (
                            <tbody>
                              <tr>
                                <td>{index.adjustment_item}</td>
                                <td>{index.adjustment_to}</td>
                                <td>{index.adjustment_type}</td>
                                <td className="invoice-description-col">
                                  <FlatFeeNoteCell note={index.notes} />
                                </td>
                                <td>{dollarFormat(index.basic_amount)}</td>
                                <td>{index.percentage}</td>
                                <td>{dollarFormat(index.amount)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                        <tfoot className="">
                          <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td className="text-bold">Total Amount</td>
                            <td className="text-bold">Addition {dollarFormat(invoiceDetails?.adjustments?.addition_amount)}</td>
                            <td className="fw-bold">Discount {dollarFormat(invoiceDetails?.adjustments?.discount_amount)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!!invoiceDetails?.settlement && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Settlement</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped mt-3">
                        <thead>
                          <tr>
                            <th>Medical Bill Balance</th>
                            <th>Total Legal Fee</th>
                            <th>Total Case Expenses</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{dollarFormat(invoiceDetails?.settlement?.balance_owed)}</td>
                            <td>{dollarFormat(invoiceDetails?.settlement?.attorney_fee_amount)}</td>
                            <td>{dollarFormat(invoiceDetails?.settlement?.total_case_expense)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.contingency?.contingency_data &&
                invoiceDetails?.contingency?.contingency_data.length > 0 &&
                typeof invoiceDetails?.settlement === 'undefined' && (
                  <div className="d-flex flex-wrap time-entries">
                    <div className="col-12 caption-bold mb-2 mt-4">Contingency Fee</div>
                    <div className="col-12 card-bg">
                      <div className="row">
                        <div className="col-12 col-md-6">Settlement Amount</div>
                        <div className="col-12 col-md-6">{dollarFormat(invoiceDetails?.contingency?.settled_amount)}</div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6">Litigation</div>
                        <div className="col-12 col-md-6">{invoiceDetails?.contingency?.is_litigation ? 'Yes' : 'No'}</div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-striped mt-3">
                          <thead>
                            <tr>
                              <th>Date of Invoice</th>
                              <th>Contingency Fee</th>
                              <th>Attorney Fee</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          {invoiceDetails?.contingency?.contingency_data.map((index) => {
                            return (
                              <tbody>
                                <tr>
                                  <td>
                                    {index.date
                                      ? handleDateTimeOffset(userDetails?.timezone, index.date, constants.month_date_year_format)
                                      : ''}
                                  </td>
                                  <td className="invoice-description-col">
                                    <FlatFeeNoteCell note={index.notes} />
                                  </td>
                                  <td>{dollarFormat(index.rate)}</td>
                                  <td>{dollarFormat(index.total)}</td>
                                </tr>
                              </tbody>
                            );
                          })}
                          <tfoot className="">
                            <tr>
                              <td></td>
                              <td></td>
                              <td className="text-bold">Total Amount</td>
                              <td className="text-bold">{dollarFormat(invoiceDetails?.contingency?.total_amount)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

              {invoiceDetails?.payment_plan?.payment_plan_data && invoiceDetails?.payment_plan?.payment_plan_data.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Payment Plan</div>
                  <div className="col-12 col-md-12 input-shadow p-2 mb-4 bg-white">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Fee</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.payment_plan?.payment_plan_data.map((index) => {
                          return (
                            <tbody>
                              <tr>
                                <td>
                                  {index.date
                                    ? handleDateTimeOffset(userDetails?.timezone, index.date, constants.month_date_year_format)
                                    : ''}
                                </td>
                                <td>{dollarFormat(index.amount)}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                      </table>
                    </div>
                  </div>
                </div>
              )}
              {invoiceDetails?.subscription?.subscription_data && invoiceDetails?.subscription?.subscription_data.due_date && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Subscription Details</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Notes</th>
                            <th>Due Date</th>
                            <th>Actual Amount</th>
                            {!!invoiceDetails?.subscription?.tax_percentage && <th>Amount (with Tax)</th>}
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            <td>{invoiceDetails?.subscription?.subscription_data.frequency_of_payment}</td>
                            <td>{invoiceDetails?.subscription?.subscription_data.invoice_date}</td>
                            <td className="invoice-description-col">
                              <FlatFeeNoteCell note={invoiceDetails?.subscription?.subscription_data.description} />
                            </td>
                            <td>
                              {invoiceDetails?.subscription?.subscription_data.due_date
                                ? handleDateTimeOffset(
                                    userDetails?.timezone,
                                    invoiceDetails?.subscription?.subscription_data.due_date,
                                    constants.month_date_year_format
                                  )
                                : ''}
                            </td>
                            <td>{dollarFormat(invoiceDetails?.subscription?.subscription_data.rate)}</td>
                            {!!invoiceDetails?.subscription?.tax_percentage && (
                              <td>{dollarFormat(invoiceDetails?.subscription?.subscription_data.amount_with_tax)}</td>
                            )}
                          </tr>
                        </tbody>

                        <tfoot>
                          {!!invoiceDetails?.subscription?.total_amount && (
                            <tr>
                              <td></td>
                              <td></td>
                              <td></td>
                              {!!invoiceDetails?.subscription?.tax_percentage ? (
                                <>
                                  <td></td>
                                  <td className="text-bold">Total Amount</td>
                                  <td className="text-bold">{dollarFormat(invoiceDetails?.subscription?.total_amount)}</td>
                                </>
                              ) : (
                                <>
                                  <td className="text-bold">Total Amount</td>
                                  <td className="text-bold">{dollarFormat(invoiceDetails?.subscription?.total_amount)}</td>
                                </>
                              )}
                            </tr>
                          )}
                          {!!invoiceDetails?.subscription?.tax_percentage && (
                            <tr>
                              <td className="disclaimer">Tax Rate @ {invoiceDetails?.subscription?.tax_percentage}%</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              {invoiceDetails?.unpaid_invoices?.unpaid_invoice_data && invoiceDetails?.unpaid_invoices?.unpaid_invoice_data.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Unpaid Invoice Balance Forward</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Invoice #</th>
                            <th>Balance Due</th>
                            <th>Amount Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceDetails?.unpaid_invoices?.unpaid_invoice_data.map((index) => {
                            return (
                              <tr>
                                <td>{index.invoice_number}</td>
                                <td>{dollarFormat(index.due_amount)}</td>
                                <td>{dollarFormat(index.paid_amount)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {invoiceDetails?.trust_account_data?.amount_paid && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Available Trust Funds</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Client Name</th>
                            <th>Account</th>
                            <th>Amount Paid</th>
                            <th>Deposit Into</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{invoiceDetails?.trust_account_data?.client_name}</td>
                            <td>{invoiceDetails?.trust_account_data?.debit_account_name}</td>
                            <td>{dollarFormat(invoiceDetails?.trust_account_data?.amount_paid)}</td>
                            <td>{invoiceDetails?.trust_account_data?.credit_account_name}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              {invoiceDetails?.payment_record_data?.length > 0 && (
                <div className="d-flex flex-wrap time-entries">
                  <div className="col-12 caption-bold mb-2 mt-4">Payment History</div>
                  <div className="col-12 card-bg">
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Entered By</th>
                            <th>Payment Method</th>
                            <th>Amount</th>
                            <th>Deposit Into</th>
                            <th>Deposit Account Name</th>
                          </tr>
                        </thead>
                        {invoiceDetails?.payment_record_data?.map((index) => {
                          let name = index?.created_by_details?.first_name;
                          if (index?.created_by_details?.last_name) name = name + ' ' + index?.created_by_details?.last_name;
                          let hour_rate = index.hour_rate;
                          hour_rate = hour_rate ? parseFloat(hour_rate) : 0;
                          let total_hours = index.total_hours;
                          total_hours = total_hours ? parseFloat(total_hours) : 0;
                          let total = hour_rate * total_hours;
                          return (
                            <tbody>
                              <tr>
                                <td>
                                  {index?.payment_record_date
                                    ? handleDateTimeOffset(
                                        userDetails?.timezone,
                                        index?.payment_record_date,
                                        constants.month_date_year_format
                                      )
                                    : ''}
                                </td>
                                <td>{name}</td>
                                <td>{index?.payment_method}</td>
                                <td>{dollarFormat(index?.payment_record_amount)}</td>
                                <td>{toCapitalized(index?.deposit_into)}</td>
                                <td>{index?.deposit_account_name}</td>
                              </tr>
                            </tbody>
                          );
                        })}
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <div className="col-12 col-md-4 F-size14">
                <div className="col-12 caption-bold mb-2 mt-4 ">Total</div>
                {typeof invoiceDetails?.settlement !== 'undefined' &&
                invoiceDetails?.payment_structure.toLowerCase() === 'contingency fee' ? (
                  <div className="card-bg p-3">
                    {typeof invoiceDetails?.contingency?.settled_amount !== 'undefined' && (
                      <div className="d-flex py-2">
                        <div className="col-6">Settlement Amount</div>
                        <div className="col-6 text-bold d-flex justify-content-end">
                          {dollarFormat(invoiceDetails?.contingency?.settled_amount)}
                        </div>
                      </div>
                    )}
                    {typeof invoiceDetails?.settlement?.balance_owed !== 'undefined' &&
                      typeof invoiceDetails?.settlement?.total_case_expense !== 'undefined' && (
                        <div className="d-flex py-2">
                          <div className="col-6">Total Bills And Expenses</div>
                          <div className="col-6 text-bold d-flex justify-content-end">
                            {dollarFormat(
                              parseFloat(invoiceDetails?.settlement?.balance_owed) +
                                parseFloat(invoiceDetails?.settlement?.total_case_expense)
                            )}
                          </div>
                        </div>
                      )}
                    {typeof invoiceDetails?.settlement?.attorney_fee_amount !== 'undefined' && (
                      <div className="d-flex py-2">
                        <div className="col-6">Total Legal Fee</div>
                        <div className="col-6 text-bold d-flex justify-content-end">
                          {dollarFormat(invoiceDetails?.settlement?.attorney_fee_amount)}
                        </div>
                      </div>
                    )}
                    <div className="d-flex py-2">
                      <div className="col-6">Total to Client</div>
                      <div className="col-6 text-bold d-flex justify-content-end">
                        {dollarFormat(invoiceDetails?.settlement?.check_to_client)}
                      </div>
                    </div>
                  </div>
                ) : (
                  //  (
                  //   <div className="card-bg p-3 bg-white">
                  //     {invoiceDetails?.flat_fees?.total_amount > 0 &&
                  //       invoiceDetails?.flat_fees?.total_amount !== invoiceDetails.total_amount && (
                  //         <div className="d-flex py-2">
                  //           <div className="col-6">Flat Fees Sub-Total</div>
                  //           <div className="col-6 text-bold">{dollarFormat(invoiceDetails?.flat_fees?.total_amount)}</div>
                  //         </div>
                  //       )}

                  //     {invoiceDetails?.time_entries?.total_amount_with_tax > 0 ||
                  //       (invoiceDetails?.time_entries?.total_amount > 0 &&
                  //         invoiceDetails?.time_entries?.total_amount !== invoiceDetails.total_amount && (
                  //           <div className="d-flex py-2">
                  //             <div className="col-6">Time Entries Sub-Total</div>
                  //             <div className="col-6 text-bold">
                  //               {dollarFormat(
                  //                 invoiceDetails?.time_entries?.total_amount_with_tax || invoiceDetails?.time_entries?.total_amount
                  //               )}
                  //             </div>
                  //           </div>
                  //         ))}

                  //     {invoiceDetails?.expenses?.total_amount > 0 && (
                  //       <div className="d-flex py-2">
                  //         <div className="col-6">Expense Sub-Total</div>
                  //         <div className="col-6 text-bold">{dollarFormat(invoiceDetails?.expenses?.total_amount)}</div>
                  //       </div>
                  //     )}
                  //     {invoiceDetails.sub_total && invoiceDetails.sub_total > 0 && (
                  //       <div className="d-flex py-2">
                  //         <div className="col-6">Sub-Total</div>
                  //         <div className="col-6 text-bold">{dollarFormat(invoiceDetails.sub_total)}</div>
                  //       </div>
                  //     )}
                  //     {invoiceDetails?.unpaid_invoices?.total_amount && (
                  //       <div className="d-flex py-2">
                  //         <div className="col-6">Forwarded Sub-Total</div>
                  //         <div className="col-6 text-bold">{dollarFormat(invoiceDetails?.unpaid_invoices?.total_amount)}</div>
                  //       </div>
                  //     )}
                  //     {invoiceDetails?.adjustments?.discount_amount > 0 && (
                  //       <div className="d-flex py-2">
                  //         <div className="col-6">Discounts</div>
                  //         <div className="col-6 text-bold">{dollarFormat(invoiceDetails?.adjustments?.discount_amount)}</div>
                  //       </div>
                  //     )}
                  //     {invoiceDetails?.adjustments?.addition_amount > 0 && (
                  //       <div className="d-flex py-2">
                  //         <div className="col-6">Addition</div>
                  //         <div className="col-6 text-bold">{dollarFormat(invoiceDetails?.adjustments?.addition_amount)}</div>
                  //       </div>
                  //     )}
                  //     <div className="d-flex py-2">
                  //       <div className="col-6">Total</div>
                  //       <div className="col-6 text-bold">{dollarFormat(invoiceDetails.total_amount)}</div>
                  //     </div>
                  //     <div className="d-flex py-2">
                  //       <div className="col-6">Amount Paid</div>
                  //       <div className="col-6 text-bold">{dollarFormat(invoiceDetails.paid_amount)}</div>
                  //     </div>
                  //     <div className="d-flex py-2">
                  //       <div className="col-6">Balance Due</div>
                  //       <div className="col-6 text-bold">{dollarFormat(invoiceDetails.due_amount)}</div>
                  //     </div>
                  //   </div>
                  // )}
                  (() => {
                    const flatFee = invoiceDetails?.flat_fees?.total_amount || 0;
                    const timeEntriesFee =
                      invoiceDetails?.time_entries?.total_amount_with_tax || invoiceDetails?.time_entries?.total_amount || 0;
                    const expensesFee = invoiceDetails?.expenses?.total_amount || 0;
                    const subTotal = invoiceDetails?.sub_total || 0;
                    const forwardedFee = invoiceDetails?.unpaid_invoices?.total_amount || 0;
                    const totalAmount = invoiceDetails?.total_amount || 0;

                    const nonZeroValues = [flatFee, timeEntriesFee, expensesFee, subTotal, forwardedFee].filter((a) => a > 0);

                    const allEqualToTotal =
                      nonZeroValues.length > 0 && nonZeroValues.every((val) => val === nonZeroValues[0] && val === totalAmount);

                    return (
                      <div className="card-bg p-3">
                        {!allEqualToTotal && flatFee > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">
                              {invoiceDetails?.payment_structure?.toLowerCase() === paymentStructure.FLAT_FEE?.toLowerCase()
                                ? 'Flat Fees Sub-Total'
                                : 'Retainer Fees Sub-Total'}
                            </div>
                            <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(flatFee)}</div>
                          </div>
                        )}

                        {!allEqualToTotal && timeEntriesFee > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Time Entries Sub-Total</div>
                            <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(timeEntriesFee)}</div>
                          </div>
                        )}

                        {!allEqualToTotal && expensesFee > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Expense Sub-Total</div>
                            <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(expensesFee)}</div>
                          </div>
                        )}

                        {!allEqualToTotal && subTotal > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Sub-Total</div>
                            <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(subTotal)}</div>
                          </div>
                        )}

                        {!allEqualToTotal && forwardedFee > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Forwarded Sub-Total</div>
                            <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(forwardedFee)}</div>
                          </div>
                        )}

                        {invoiceDetails?.adjustments?.discount_amount > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Discounts</div>
                            <div className="col-6 text-bold d-flex justify-content-end">
                              {dollarFormat(invoiceDetails.adjustments.discount_amount)}
                            </div>
                          </div>
                        )}
                        {invoiceDetails?.adjustments?.addition_amount > 0 && (
                          <div className="d-flex py-2">
                            <div className="col-6">Addition</div>
                            <div className="col-6 text-bold d-flex justify-content-end">
                              {dollarFormat(invoiceDetails.adjustments.addition_amount)}
                            </div>
                          </div>
                        )}

                        <div className="d-flex py-2">
                          <div className="col-6">Total</div>
                          <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(totalAmount)}</div>
                        </div>

                        <div className="d-flex py-2">
                          <div className="col-6">Amount Paid</div>
                          <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(invoiceDetails.paid_amount)}</div>
                        </div>
                        <div className="d-flex py-2">
                          <div className="col-6">Balance Due</div>
                          <div className="col-6 text-bold d-flex justify-content-end">{dollarFormat(invoiceDetails.due_amount)}</div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
            {invoiceDetails?.payment_request_data && (
              <div className="d-flex flex-wrap time-entries">
                <div className="col-12 caption-bold mb-2 mt-4">Payment Request</div>
                <div className="col-12 card-bg">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Request ID</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                          <th>Deposit Into</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{invoiceDetails?.payment_request_data?.payment_request_number}</td>
                          <td>{dollarFormat(invoiceDetails?.payment_request_data?.amount)}</td>
                          <td>
                            {handleDateTimeOffset(
                              userDetails?.timezone,
                              invoiceDetails?.payment_request_data?.due_date,
                              constants.month_date_year_format
                            )}
                          </td>
                          <td>{invoiceDetails?.payment_request_data?.deposit_account_name}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
